import React, { useRef } from "react";
import {
  Droplets,
  ShieldAlert,
  ListChecks,
  Search,
  CreditCard,
  Smile,
  Globe,
  Phone,
  MessageCircle,
  ArrowRight,
  CheckCircle2,
  FileText,
} from "lucide-react";
import { motion, useInView } from "framer-motion";

/* ─── Fade-in wrapper ─────────────────────────────── */
const FadeIn = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/* ─── WhatsApp SVG ────────────────────────────────── */
const WhatsAppIcon = ({ size = 20 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width={size} height={size} fill="currentColor">
    <path d="M16.004 2.667C8.64 2.667 2.667 8.64 2.667 16c0 2.347.64 4.64 1.853 6.64L2.667 29.333l6.907-1.813A13.267 13.267 0 0 0 16.004 29.333c7.36 0 13.33-5.973 13.33-13.333 0-7.36-5.97-13.333-13.33-13.333Zm0 24.267a11 11 0 0 1-5.6-1.534l-.4-.24-4.107 1.08 1.094-4-.267-.413A11.02 11.02 0 0 1 4.987 16c0-6.08 4.947-11.027 11.017-11.027 6.08 0 11.027 4.947 11.027 11.027 0 6.08-4.947 11.027-11.027 11.027Zm6.053-8.24c-.333-.16-1.96-.96-2.267-1.067-.306-.107-.52-.16-.747.16-.213.32-.84 1.067-1.04 1.28-.187.213-.387.24-.72.08-.333-.16-1.413-.52-2.693-1.653-.987-.88-1.653-1.973-1.853-2.307-.187-.333-.02-.507.147-.667.147-.147.333-.387.507-.573.173-.187.213-.32.32-.533.107-.213.053-.4-.027-.56-.08-.16-.747-1.787-1.027-2.453-.267-.64-.547-.56-.747-.56h-.64c-.213 0-.56.08-.853.4-.293.32-1.12 1.093-1.12 2.667 0 1.573 1.147 3.093 1.307 3.307.16.213 2.24 3.44 5.44 4.827.76.32 1.36.507 1.813.653.76.24 1.453.2 2 .12.613-.093 1.88-.773 2.147-1.52.267-.747.267-1.387.187-1.52-.08-.133-.293-.213-.627-.373Z" />
  </svg>
);

/* ─── Audit Items ─────────────────────────────────── */
const auditItems = [
  {
    icon: Droplets,
    iconBg: "bg-blue-500",
    label: "Water Problems",
    desc: "Detecting pipe leakages, tap malfunctions, and wall seepage early before they become costly repairs.",
  },
  {
    icon: ShieldAlert,
    iconBg: "bg-orange-500",
    label: "Damage Control",
    desc: "Checking wall paint, flooring, and fixtures for tenant wear-and-tear to protect your asset's value.",
  },
];

/* ─── Steps ───────────────────────────────────────── */
const steps = [
  {
    icon: ListChecks,
    number: "01",
    label: "List",
    desc: "Add your property details on housyhunt.com and get onboarded in minutes.",
    color: "bg-blue-500",
    light: "bg-blue-50 border-blue-100",
    text: "text-blue-700",
  },
  {
    icon: Search,
    number: "02",
    label: "Audit",
    desc: "We perform a professional baseline inspection of your flat at no extra charge.",
    color: "bg-amber-500",
    light: "bg-amber-50 border-amber-100",
    text: "text-amber-700",
  },
  {
    icon: CreditCard,
    number: "03",
    label: "Activate",
    desc: "Subscribe to the Expert Management Plan for ₹24,999/year — flat fee, no surprises.",
    color: "bg-red-600",
    light: "bg-red-50 border-red-100",
    text: "text-red-700",
  },
  {
    icon: Smile,
    number: "04",
    label: "Relax",
    desc: "We manage maintenance and find tenants. You collect rent directly — always.",
    color: "bg-emerald-500",
    light: "bg-emerald-50 border-emerald-100",
    text: "text-emerald-700",
  },
];

/* ─── Contact Numbers ─────────────────────────────── */
const phones = [
  { number: "+91 8088687429", href: "tel:+918088687429" },
  { number: "+91 9739580142", href: "tel:+919739580142" },
];

/* ─── Main Component ──────────────────────────────── */
const AuditAndContact = () => {
  return (
    <section className="w-full bg-gray-50 py-14 px-4 sm:px-6">
      <div className="max-w-5xl mx-auto space-y-20">

        {/* ══════════════════════════════════
            INTERIOR & LEAKAGE AUDITS
        ══════════════════════════════════ */}
        <div>
          <FadeIn>
            <p className="text-red-600 text-xs font-bold tracking-widest uppercase mb-2">
              Protocol 02
            </p>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
              Interior &amp; Leakage Audits
            </h2>
            <p className="text-gray-500 text-sm leading-relaxed max-w-xl mb-10">
              Our executives perform thorough physical audits of your property,
              catching problems early so minor issues never become major expenses.
            </p>
          </FadeIn>

          {/* Audit cards + image side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* LEFT — audit cards stacked */}
            <div className="flex flex-col gap-5">
              {auditItems.map((item, i) => {
                const Icon = item.icon;
                return (
                  <FadeIn key={item.label} delay={i * 0.08}>
                    <motion.div
                      whileHover={{ y: -4 }}
                      className="bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md hover:border-red-100 transition-all duration-300 p-6 flex gap-5 items-start"
                    >
                      <div
                        className={`w-12 h-12 rounded-2xl ${item.iconBg} flex items-center justify-center shadow-md shrink-0`}
                      >
                        <Icon size={22} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-base font-bold text-gray-900 mb-1.5">
                          {item.label}
                        </h3>
                        <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                      </div>
                    </motion.div>
                  </FadeIn>
                );
              })}

              {/* What we check checklist */}
              <FadeIn delay={0.18}>
                <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">
                    What We Check
                  </p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
                    {[
                      "Pipe leakages",
                      "Tap malfunctions",
                      "Wall seepage",
                      "Ceiling stains",
                      "Wall paint condition",
                      "Flooring integrity",
                      "Electrical fixtures",
                      "Door & window frames",
                    ].map((check) => (
                      <div key={check} className="flex items-center gap-2">
                        <CheckCircle2 size={14} className="text-red-500 shrink-0" />
                        <span className="text-gray-600 text-xs">{check}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </FadeIn>
            </div>

            {/* RIGHT — image */}
            <FadeIn delay={0.1} className="relative">
              <div className="relative rounded-2xl overflow-hidden h-full min-h-[320px] shadow-lg">
                <img
                  src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80&fit=crop"
                  alt="Property inspection"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />

                {/* Floating badge */}
                <div className="absolute top-4 left-4">
                  <span className="bg-white/90 backdrop-blur-sm text-red-600 text-xs font-bold px-4 py-2 rounded-full shadow">
                    Physical Executive Visits
                  </span>
                </div>

                {/* Bottom overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <p className="text-white font-bold text-base mb-1">
                    Proactive, not reactive.
                  </p>
                  <p className="text-white/75 text-xs leading-snug">
                    We identify issues before they escalate, protecting your property value and tenant satisfaction.
                  </p>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>

        {/* ══════════════════════════════════
            HOW TO GET STARTED
        ══════════════════════════════════ */}
        <div>
          <FadeIn>
            <p className="text-red-600 text-xs font-bold tracking-widest uppercase mb-2">
              Simple 4-Step Process
            </p>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
              How to Get Started
            </h2>
            <p className="text-gray-500 text-sm leading-relaxed max-w-xl mb-10">
              Getting started with HousyHunt takes minutes. We handle the rest.
            </p>
          </FadeIn>

          {/* Steps grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 relative">
            {/* Connector line — desktop only */}
            <div className="hidden lg:block absolute top-9 left-[12.5%] right-[12.5%] h-px bg-gradient-to-r from-blue-200 via-amber-200 via-red-200 to-emerald-200 z-0" />

            {steps.map((step, i) => {
              const Icon = step.icon;
              return (
                <FadeIn key={step.label} delay={i * 0.08}>
                  <motion.div
                    whileHover={{ y: -5 }}
                    className="relative z-10 bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-lg hover:border-gray-200 transition-all duration-300 p-6 flex flex-col items-center text-center gap-4"
                  >
                    {/* Step icon */}
                    <div
                      className={`w-14 h-14 rounded-2xl ${step.color} flex items-center justify-center shadow-lg shrink-0`}
                    >
                      <Icon size={24} className="text-white" />
                    </div>

                    {/* Step number */}
                    <div>
                      <span
                        className={`text-xs font-extrabold tracking-widest uppercase ${step.text}`}
                      >
                        Step {step.number}
                      </span>
                      <h3 className="text-base font-bold text-gray-900 mt-1 mb-2">
                        {step.label}
                      </h3>
                      <p className="text-gray-500 text-xs leading-relaxed">{step.desc}</p>
                    </div>
                  </motion.div>
                </FadeIn>
              );
            })}
          </div>

          {/* CTA under steps */}
          <FadeIn delay={0.35}>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <motion.a
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                href="https://www.housyhunt.com"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-3.5 rounded-full font-semibold text-sm shadow-lg shadow-red-600/25 transition-all duration-300"
              >
                List Your Property Now
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </motion.a>
              <a
                href="https://wa.me/message/MDG2ZSZNG6P6G1"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-[#25D366] hover:bg-[#1EBE5B] text-white px-7 py-3.5 rounded-full font-semibold text-sm shadow-lg shadow-green-500/25 transition-all duration-300"
              >
                <WhatsAppIcon size={16} />
                Chat on WhatsApp
              </a>
            </div>
          </FadeIn>
        </div>

        {/* ══════════════════════════════════
            CONTACT US
        ══════════════════════════════════ */}
        <FadeIn>
          <div className="relative overflow-hidden rounded-3xl">
            {/* BG image */}
            <div className="absolute inset-0 z-0">
              <img
                src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1400&q=80&fit=crop"
                alt="Office"
                className="w-full h-full object-cover opacity-10"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-900/97 to-red-950/90" />
            </div>

            <div className="relative z-10 px-8 sm:px-12 py-12 sm:py-16">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">

                {/* LEFT — text */}
                <div>
                  <p className="text-red-400 text-xs font-bold tracking-widest uppercase mb-3">
                    Get In Touch
                  </p>
                  <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4 leading-snug">
                    Ready to hand over the<br />
                    <span className="text-red-400">stress of property management?</span>
                  </h2>
                  <p className="text-gray-400 text-sm leading-relaxed max-w-md">
                    Our team is available to answer every question — from subscription details
                    to tenant management. Reach out, and we'll get back to you within the hour.
                  </p>
                </div>

                {/* RIGHT — contact cards */}
                <div className="flex flex-col gap-4">

                  {/* WhatsApp Support */}
                  <a
                    href="https://wa.me/message/MDG2ZSZNG6P6G1"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center gap-4 bg-red-600 hover:bg-red-700 border border-red-500 rounded-2xl px-5 py-4 transition-all duration-300 shadow-lg"
                  >
                    <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center shrink-0 shadow-md group-hover:scale-105 transition-transform duration-300">
                      <FileText size={20} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-red-100 text-xs font-semibold uppercase tracking-wide mb-0.5">
                        Instant Assistance
                      </p>
                      <p className="text-white font-bold text-sm">
                        Chat on WhatsApp
                      </p>
                    </div>
                    <ArrowRight size={16} className="text-white group-hover:translate-x-1 transition-all duration-200" />
                  </a>

                  {/* Website */}
                  <a
                    href="https://www.housyhunt.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center gap-4 bg-white/8 hover:bg-white/12 border border-white/10 hover:border-white/20 rounded-2xl px-5 py-4 transition-all duration-300"
                  >
                    <div className="w-11 h-11 rounded-xl bg-red-600 flex items-center justify-center shrink-0 shadow-md group-hover:scale-105 transition-transform duration-300">
                      <Globe size={20} className="text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="text-gray-400 text-xs font-semibold uppercase tracking-wide mb-0.5">
                        Visit Us Online
                      </p>
                      <p className="text-white font-bold text-sm group-hover:text-red-300 transition-colors duration-200">
                        www.housyhunt.com
                      </p>
                    </div>
                    <ArrowRight size={16} className="text-gray-600 group-hover:text-red-400 group-hover:translate-x-1 transition-all duration-200" />
                  </a>

                  {/* Phone numbers */}
                  {phones.map((p, i) => (
                    <a
                      key={p.number}
                      href={p.href}
                      className="group flex items-center gap-4 bg-white/8 hover:bg-white/12 border border-white/10 hover:border-white/20 rounded-2xl px-5 py-4 transition-all duration-300"
                    >
                      <div className="w-11 h-11 rounded-xl bg-gray-700 flex items-center justify-center shrink-0 shadow-md group-hover:scale-105 transition-transform duration-300">
                        <Phone size={18} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="text-gray-400 text-xs font-semibold uppercase tracking-wide mb-0.5">
                          Call Us
                        </p>
                        <p className="text-white font-bold text-sm group-hover:text-red-300 transition-colors duration-200">
                          {p.number}
                        </p>
                      </div>
                      <ArrowRight size={16} className="text-gray-600 group-hover:text-red-400 group-hover:translate-x-1 transition-all duration-200" />
                    </a>
                  ))}

                  {/* WhatsApp */}
                  <a
                    href="https://wa.me/message/MDG2ZSZNG6P6G1"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center gap-4 bg-[#25D366]/15 hover:bg-[#25D366]/25 border border-[#25D366]/25 hover:border-[#25D366]/40 rounded-2xl px-5 py-4 transition-all duration-300"
                  >
                    <div className="w-11 h-11 rounded-xl bg-[#25D366] flex items-center justify-center shrink-0 shadow-md group-hover:scale-105 transition-transform duration-300">
                      <WhatsAppIcon size={20} />
                    </div>
                    <div className="flex-1">
                      <p className="text-green-400 text-xs font-semibold uppercase tracking-wide mb-0.5">
                        WhatsApp Us
                      </p>
                      <p className="text-white font-bold text-sm">
                        +91 8088687429 · +91 9739580142
                      </p>
                    </div>
                    <div className="relative flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-60"></span>
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-400"></span>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </FadeIn>

      </div>
    </section>
  );
};

export default AuditAndContact;